import React, { useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import './AboutGallery.css';

const photos = [
  { id: 'panoramic', alt: 'Elevador panorâmico de vidro em hall claro, com estrutura metálica escura, escada e vegetação' },
  { id: 'stainless', alt: 'Elevador em inox aberto em uma entrada sofisticada de mármore claro' },
  { id: 'warm-cabin', alt: 'Cabina moderna envidraçada integrada à arquitetura com iluminação quente' },
];
const srcSet = (id, layout, widths) => widths.map(width => `/images/about-gallery/${id}-${layout}-${width}.webp ${width}w`).join(', ');
const sizes = '(max-width: 374px) calc(100vw - 32px), (max-width: 767px) calc(100vw - 40px), (max-width: 1023px) calc((100vw - 91px) * 0.474), (max-width: 1199px) calc((100vw - 135px) * 0.485), (max-width: 1391px) calc((100vw - 212px) * 0.485), 572px';

export const AboutGallery = () => {
  const [viewportRef, api] = useEmblaCarousel({ loop: true, align: 'start' });
  const [selected, setSelected] = useState(0);
  useEffect(() => {
    if (!api) return;
    const update = () => setSelected(api.selectedScrollSnap());
    update(); api.on('select', update); api.on('reInit', update);
    return () => { api.off('select', update); api.off('reInit', update); };
  }, [api]);
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const previous = () => api?.scrollPrev(reducedMotion());
  const next = () => api?.scrollNext(reducedMotion());
  const onKeyDown = event => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const actions = { ArrowLeft: previous, ArrowRight: next, Home: () => api?.scrollTo(0, reducedMotion()), End: () => api?.scrollTo(2, reducedMotion()) };
    if (actions[event.key]) { event.preventDefault(); event.stopPropagation(); actions[event.key](); }
  };
  return (
    <div className="about-gallery" role="region" aria-roledescription="carrossel" aria-label="Galeria Sobre a FATT"
      tabIndex={0} aria-keyshortcuts="ArrowLeft ArrowRight Home End" onKeyDown={onKeyDown}
      data-testid="about-gallery" data-current-slide={selected + 1} data-total-slides={photos.length}>
      <div className="about-gallery-viewport" id="about-gallery-viewport" ref={viewportRef} data-testid="about-gallery-viewport">
        <div className="about-gallery-track">{photos.map((photo, index) => (
          <div className="about-gallery-slide" key={photo.id} role="group" aria-roledescription="slide"
            aria-label={`Foto ${index + 1} de 3`} aria-hidden={selected !== index} data-testid={`about-gallery-slide-${index + 1}`}>
            <picture>
              <source media="(max-width: 767px)" srcSet={srcSet(photo.id, 'mobile', [400, 700, 1000])} sizes={sizes} />
              <source media="(max-width: 1023px)" srcSet={srcSet(photo.id, 'tablet', [320, 480, 610])} sizes={sizes} />
              <img src={`/images/about-gallery/${photo.id}-desktop-600.webp`} srcSet={srcSet(photo.id, 'desktop', [400, 600, 898])}
                sizes={sizes} alt={photo.alt} loading="lazy" decoding="async" draggable={false} width="898" height="847"
                data-testid={`about-gallery-image-${index + 1}`} />
            </picture>
          </div>
        ))}</div>
      </div>
      <Button type="button" variant="outline" size="icon" className="about-gallery-arrow about-gallery-previous" disabled={!api}
        onClick={previous} aria-label="Foto anterior — Sobre a FATT" aria-controls="about-gallery-viewport" data-testid="about-gallery-prev"><ChevronLeft size={20} aria-hidden="true" /></Button>
      <Button type="button" variant="outline" size="icon" className="about-gallery-arrow about-gallery-next" disabled={!api}
        onClick={next} aria-label="Próxima foto — Sobre a FATT" aria-controls="about-gallery-viewport" data-testid="about-gallery-next"><ChevronRight size={20} aria-hidden="true" /></Button>
      <div className="about-gallery-tools">
        <div className="about-gallery-dots" role="group" aria-label="Escolher foto — Sobre a FATT">{photos.map((photo, index) => (
          <button type="button" key={photo.id} onClick={() => api?.scrollTo(index, reducedMotion())}
            aria-label={`Ver foto ${index + 1} de 3: ${photo.alt}`} aria-current={selected === index ? 'true' : undefined}
            aria-controls="about-gallery-viewport" data-testid={`about-gallery-dot-${index + 1}`}><span /></button>
        ))}</div>
        <span className="about-gallery-counter" role="status" aria-live="polite" aria-atomic="true" data-testid="about-gallery-counter"><span className="sr-only">Foto </span>{selected + 1} / 3</span>
      </div>
    </div>
  );
};